import { getPersonaRepo, type AgentPersona } from '../shared/types.js';

/**
 * Fuzzy-matches a query against a persona and returns a numeric score.
 * Higher is better; zero means the persona should be excluded.
 *
 * The scoring ladder is intentionally simple:
 *   - 100 — persona name starts with the query (case-insensitive)
 *   - 60  — a word in the name starts with the query
 *   - 40  — the query appears anywhere in the name
 *   - 20  — the query matches description / agentType / model / repoSlug
 *   - 1   — empty query (all personas are included, recency tie-break wins)
 */
export function scorePersona(persona: AgentPersona, query: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const name = persona.name.toLowerCase();

  if (name.startsWith(q)) return 100;

  const nameWords = name.split(/\s+/);
  if (nameWords.some((w) => w.startsWith(q))) return 60;

  if (name.includes(q)) return 40;

  const repo = getPersonaRepo(persona);
  const haystacks = [
    persona.description ?? '',
    persona.agentType,
    persona.model ?? '',
    repo?.repoSlug ?? '',
  ];
  if (haystacks.some((h) => h.toLowerCase().includes(q))) return 20;

  return 0;
}

/**
 * Ranks personas against `query`, drops non-matches, and returns the top
 * `limit` entries — or every match when `limit` is omitted. Ties are broken by
 * most-recently-updated first.
 */
export function rankPersonas(
  personas: AgentPersona[],
  query: string,
  limit?: number,
): AgentPersona[] {
  const scored = personas
    .map((persona) => ({ persona, score: scorePersona(persona, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.persona.updatedAt - a.persona.updatedAt);
  return scored.slice(0, limit).map((entry) => entry.persona);
}
